import { PropertyData } from "../to-be-removed/LocalData/Property/PropertyData";
import { UserData } from "../to-be-removed/LocalData/User/UserData";
import { ContractorGroupData } from "../to-be-removed/LocalData/ContractorGroup/ContractorGroupData";
import { LocalData } from "../to-be-removed/LocalData/General/LocalData";
import { ActionValidatorIdentifier, StaticActionValidator } from "./useActionValidatorRights";
import { actionValidatorJSON } from "@src/auth/actionValidatorJSON";

interface LimitationGroup {
  identifier: string;
  canPerformAction: (properties?: PropertyData[], user?: UserData, contractors?: ContractorGroupData[]) => boolean;
}

class LimitationGroup_CurrentUser implements LimitationGroup {
  identifier: string = "current_user";
  canPerformAction(properties?: PropertyData[], user?: UserData) {
    return LocalData.currentUser === user;
  }
}
class LimitationGroup_OperationManager implements LimitationGroup {
  identifier: string = "operation_manager";
  canPerformAction(properties?: PropertyData[]) {
    for (let property of properties ?? LocalData.currentUser.properties) {
      if (!property.deleted && property?.members.getData(LocalData.currentUser.id)?.isOperationManager) {
        return true;
      }
    }
    return false;
  }
}
class LimitationGroup_Landlord_Admin implements LimitationGroup {
  identifier: string = "landlord_admin";
  canPerformAction(properties?: PropertyData[], user?: UserData) {
    for (let property of properties ?? LocalData.currentUser.properties) {
      if (
        !property.deleted &&
        property?.checkForLandlordRoles(LocalData.currentUser) &&
        property?.members.getData(LocalData.currentUser.id)?.isAdmin
      ) {
        return true;
      }
    }
    return false;
  }
}
class LimitationGroup_Landlord implements LimitationGroup {
  identifier: string = "landlord";
  canPerformAction(properties?: PropertyData[], user?: UserData) {
    for (let property of properties ?? LocalData.currentUser.properties) {
      if (!property.deleted && property?.checkForLandlordRoles(LocalData.currentUser)) {
        return true;
      }
    }
    return false;
  }
}
class LimitationGroup_Tenant implements LimitationGroup {
  identifier: string = "tenant";
  canPerformAction(properties?: PropertyData[], user?: UserData) {
    for (let property of properties ?? LocalData.currentUser.properties) {
      if (!property.deleted && property?.checkForTenantRoles(LocalData.currentUser)) {
        return true;
      }
    }
    return false;
  }
}
class LimitationGroup_Tenant_Admin implements LimitationGroup {
  identifier: string = "tenant_admin";
  canPerformAction(properties?: PropertyData[], user?: UserData) {
    for (let property of properties ?? LocalData.currentUser.properties) {
      if (!property.deleted && property?.checkForTenantRoles(LocalData.currentUser) && property?.members.getData(LocalData.currentUser.id)?.isAdmin) {
        return true;
      }
    }
    return false;
  }
}
class LimitationGroup_Entrepreneur_Admin implements LimitationGroup {
  identifier: string = "entrepreneur_admin";
  canPerformAction(properties?: PropertyData[], user?: UserData, contractors?: ContractorGroupData[]) {
    for (let property of properties ?? LocalData.currentUser.properties) {
      if (!property.deleted && property?.checkForEntrepreneurRoles(LocalData.currentUser)) {
        for (let contractor of contractors ?? []) {
          if (contractor?.members.getData(LocalData.currentUser.id)?.admin) {
            return true;
          }
        }
      }
    }
    return false;
  }
}

class LimitationGroup_Entrepreneur implements LimitationGroup {
  identifier: string = "entrepreneur";
  canPerformAction(properties?: PropertyData[], user?: UserData) {
    for (let property of properties ?? LocalData.currentUser.properties) {
      if (!property.deleted && property?.checkForEntrepreneurRoles(LocalData.currentUser)) {
        return true;
      }
    }
    return false;
  }
}

/**
 * @deprecated use useActionValidatorRight instead
 */
export class ActionValidator {
  static ALL_ACTIONS: LimitationAction[] = [];
  static ALL_LIMITATION_GROUPS: LimitationGroup[] = [
    new LimitationGroup_CurrentUser(),
    new LimitationGroup_OperationManager(),
    new LimitationGroup_Landlord_Admin(),
    new LimitationGroup_Landlord(),
    new LimitationGroup_Tenant_Admin(),
    new LimitationGroup_Tenant(),
    new LimitationGroup_Entrepreneur_Admin(),
    new LimitationGroup_Entrepreneur(),
  ];

  static initialize(completion: (success: boolean) => void) {
    for (let actionGroup of actionValidatorJSON) {
      let action = new LimitationAction(actionGroup.Identifier);

      for (let limitation in actionGroup) {
        if ((actionGroup as any)[limitation].toLowerCase() === "x") {
          let group = this.getLimitationGroup(limitation);
          if (group) action.allowedGroups.push(group);
        }
      }
      this.ALL_ACTIONS.push(action);
    }
    StaticActionValidator.initialize(() => {
      completion(true);
    });
  }

  private static getLimitationGroup(identifier: ActionValidatorIdentifier) {
    for (let group of this.ALL_LIMITATION_GROUPS) {
      if (group.identifier === identifier) {
        return group;
      }
    }
    return null;
  }

  static isAllowedToPerformAction(
    identifier: ActionValidatorIdentifier,
    properties?: PropertyData[],
    user?: UserData,
    contractors?: ContractorGroupData[]
  ) {
    if (LocalData.currentUser.deleted) {
      return false;
    }

    // Boolean of if we found a property with a custom actionvalidator that denied access to performing an action
    var foundIllegal = false;
    // Boolean of if we found a property without the custom actionvalidator
    var foundEmpty = false;

    const filteredProperties = (properties ?? LocalData.currentUser.properties)?.filter((p) => !p.deleted && p.name);

    for (const property of filteredProperties ?? []) {
      let foundCustomActionValidator = false;
      let customAction = property.actionValidator.actions[identifier];

      if (customAction) {
        let action = this.ALL_ACTIONS.find((action) => action.identifier === identifier);
        if (action) {
          foundCustomActionValidator = true;
          if (action.canPerformAction(filteredProperties, user, contractors, customAction.allowedGroups)) {
            return true;
          } else {
            foundIllegal = true;
          }
        }
      }
      if (!foundCustomActionValidator) {
        foundEmpty = true;
      }
    }
    if (foundIllegal && !foundEmpty) {
      return false;
    }

    for (let action of this.ALL_ACTIONS) {
      if (action.identifier === identifier) {
        return action.canPerformAction(filteredProperties, user, contractors);
      }
    }
    console.log("Error: did not find action with identifier " + identifier);
    return false;
  }
}

/**
 * @deprecated use useActionValidatorRight instead
 */
export class LimitationAction {
  identifier: ActionValidatorIdentifier;
  allowedGroups: LimitationGroup[] = [];

  constructor(identifier: ActionValidatorIdentifier) {
    this.identifier = identifier;
  }

  canPerformAction(properties?: PropertyData[], user?: UserData, contractors?: ContractorGroupData[], allowedGroups?: string[]) {
    let groups: LimitationGroup[] = [];
    if (allowedGroups) {
      for (const group of allowedGroups) {
        const groupData = ActionValidator.ALL_LIMITATION_GROUPS.find((data) => data.identifier === group);
        if (groupData) groups.push(groupData);
      }
    } else {
      groups = this.allowedGroups;
    }

    for (let group of groups) {
      if (group.canPerformAction(properties, user, contractors)) {
        return true;
      }
    }
    return false;
  }
}
